import { DataTable } from "@/components/molecules/tables/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  isReminderCancellable,
  REMINDER_CHANNEL_COLORS,
  reminderContent,
  reminderStatusVariant,
} from "@/features/reminders/display";
import {
  useCancelReminder,
  useReminders,
} from "@/features/reminders/hooks/use-reminders";
import type { Reminder } from "@/features/reminders/types";
import type { Customer } from "@/features/customers/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { cn } from "@/lib/utils";
import {
  BellRingingIcon,
  ChatCircleTextIcon,
  PhoneCallIcon,
} from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { toast } from "sonner";

/**
 * Still going to happen: due later and still in a state it can be cancelled
 * from. Everything else — sent, skipped, cancelled, failed — is history.
 */
const isUpcomingReminder = (reminder: Reminder, now = Date.now()) =>
  reminder.scheduled_at >= now && isReminderCancellable(reminder);

interface CustomerRemindersTableProps {
  customer: Customer;
  limit?: number;
}

/**
 * The follow-ups planned with this customer — what is coming, and what already went
 * out. Cancelling from here is the counterpart of the agent's own cancel tool, for
 * when the customer calls the shop instead of the agent.
 */
export function CustomerRemindersTable({
  customer,
  limit,
}: CustomerRemindersTableProps) {
  const { t } = useTranslation("reminders");
  const { format } = useDateFormat();
  const cancelReminder = useCancelReminder();

  const { data: reminders = [], isLoading } = useReminders({
    customer_id: customer.id,
  });

  const displayReminders = useMemo(() => {
    // What is still going to happen comes first, soonest first; then the history,
    // most recent first.
    const now = Date.now();
    const upcoming = reminders
      .filter((r) => isUpcomingReminder(r, now))
      .sort((a, b) => a.scheduled_at - b.scheduled_at);
    const past = reminders
      .filter((r) => !upcoming.includes(r))
      .sort((a, b) => b.scheduled_at - a.scheduled_at);

    const sorted = [...upcoming, ...past];
    return limit ? sorted.slice(0, limit) : sorted;
  }, [reminders, limit]);

  const handleCancel = async (reminder: Reminder) => {
    try {
      const cancelled = await cancelReminder.mutateAsync(reminder.id);
      if (cancelled) {
        toast.success(t("messages.cancelSuccess"));
      } else {
        toast.error(t("messages.cancelTooLate"));
      }
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t("messages.cancelError"),
      );
    }
  };

  const columns: ColumnDef<Reminder>[] = [
    {
      accessorKey: "channel",
      header: t("columns.followUp"),
      cell: ({ row }) => {
        const reminder = row.original;
        const Icon =
          reminder.channel === "sms" ? ChatCircleTextIcon : PhoneCallIcon;
        return (
          <div className="flex items-center gap-3">
            <div
              className={cn(
                "rounded-lg border p-2",
                REMINDER_CHANNEL_COLORS[reminder.channel].chip,
              )}
            >
              <Icon className="h-5 w-5" weight="fill" />
            </div>
            <div>
              <div className="font-medium">
                {t(`channel.${reminder.channel}`)}
              </div>
              {reminder.reason && (
                <div className="max-w-[320px] truncate text-xs text-muted-foreground">
                  {reminder.reason}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "scheduled_at",
      header: t("columns.when"),
      cell: ({ row }) => (
        <div className="text-sm">
          <div>{format(row.original.scheduled_at, "PPP")}</div>
          <div className="text-xs text-muted-foreground">
            {format(row.original.scheduled_at, "p")}
          </div>
        </div>
      ),
    },
    {
      accessorKey: "message",
      header: t("columns.content"),
      // What was actually sent once the follow-up was prepared, falling back to what the
      // agent planned during the conversation.
      cell: ({ row }) => (
        <div className="max-w-[360px] whitespace-pre-wrap text-sm">
          {reminderContent(row.original).text || "-"}
        </div>
      ),
    },
    {
      accessorKey: "status",
      header: t("columns.status"),
      cell: ({ row }) => {
        const reminder = row.original;
        return (
          <div className="space-y-1">
            <Badge variant={reminderStatusVariant(reminder.status)}>
              {t(`status.${reminder.status}`)}
            </Badge>
            {(reminder.skipped_reason || reminder.last_error) && (
              <div className="max-w-[240px] text-xs text-muted-foreground">
                {reminder.skipped_reason || reminder.last_error}
              </div>
            )}
          </div>
        );
      },
    },
    {
      id: "actions",
      header: () => <div className="text-right">{t("columns.actions")}</div>,
      cell: ({ row }) => {
        const reminder = row.original;
        if (!isReminderCancellable(reminder)) return null;
        return (
          <div className="text-right">
            <Button
              variant="ghost"
              size="sm"
              disabled={cancelReminder.isPending}
              onClick={() => handleCancel(reminder)}
            >
              {t("actions.cancel")}
            </Button>
          </div>
        );
      },
    },
  ];

  if (displayReminders.length === 0 && !isLoading) {
    return (
      <div className="py-8 text-center">
        <BellRingingIcon className="mx-auto mb-2 h-12 w-12 text-muted-foreground" />
        <p className="text-sm text-muted-foreground">{t("empty")}</p>
      </div>
    );
  }

  return (
    <DataTable
      variant="embedded"
      columns={columns}
      data={displayReminders}
      isLoading={isLoading}
      total={displayReminders.length}
      isRowMuted={(reminder) => !isUpcomingReminder(reminder)}
    />
  );
}
